const FALLBACK_SUFFIX = 'tribetale.com';

/**
 * The public host a given address would produce, derived from the site's own
 * URL so staging and production read correctly without a hardcoded domain.
 */
export function buildAddressHost(siteUrl: string, slug: string): string {
    try {
        const {hostname} = new URL(siteUrl);
        const parts = hostname.split('.');
        if (parts.length < 2) {
            return `${slug}.${FALLBACK_SUFFIX}`;
        }
        return [slug, ...parts.slice(1)].join('.');
    } catch {
        return `${slug}.${FALLBACK_SUFFIX}`;
    }
}

/** Trim an address to what the server will accept, as the owner types. */
export function normaliseAddress(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-/, '')
        .slice(0, 63);
}

/**
 * A validation failure arrives as a generic `message` ("Invalid request
 * parameters") with the sentence worth reading in `details`. Proved against
 * staging on 2026-10-27: GET /api/sites/:id/slug-available/bad- returns
 * exactly that shape. Show the field detail when there is one.
 */
export function preferFieldDetail(message: string, details?: Array<{field: string; message: string}>): string {
    const detail = details?.find(entry => entry?.message)?.message;
    return detail || message;
}

export function formatAddressDate(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'});
}

/**
 * The server refuses an early change with an ISO timestamp in the message.
 * Owners should read a date, not a timestamp.
 */
export function humaniseAddressError(message: string): string {
    const match = message.match(/changed again on (\S+)/);
    const readable = match ? formatAddressDate(match[1]) : '';
    if (!readable) {
        return message;
    }
    return `You changed this address recently. You can change it again on ${readable}.`;
}
